import { ResetIcon } from "../../icons/icons";
import BtnTooltip from "../../tooltips/btnTooltip";

export default class DimensionTune {
  static get isTune() {
    return true;
  }

  constructor({ api, data, config, block }) {
    this.api = api;
    this.block = block;
    this.settings = config;

    this.data = {
      dimensions: {
        width: data?.dimensions?.width ?? "auto",
        height: data?.dimensions?.height ?? "auto",
        widthUnit: data?.dimensions?.widthUnit ?? "px",
        heightUnit: data?.dimensions?.heightUnit ?? "px",
      },
    };

    // Config defaults
    this._widthConfig = {
      unitToggle: config?.width?.unitToggle ?? false,
      min: config?.width?.min ?? 0,
      max: config?.width?.max ?? null,
    };
    this._heightConfig = {
      unitToggle: config?.height?.unitToggle ?? false,
      min: config?.height?.min ?? 0,
      max: config?.height?.max ?? null,
    };

    this._CSS = {
      wrapper: "cdx-dimension-settings",
      blockWrapper: "cdx-dimension-block-wrapper",
      widthSettingContainer: "cdx-setting-width-container",
      widthLabel: "cdx-setting-width-label",
      width: "cdx-setting-width",
      heightSettingContainer: "cdx-setting-height-container",
      heightLabel: "cdx-setting-height-label",
      height: "cdx-setting-height",
      unitToggle: "cdx-setting-unit-toggle",
      tuneWrapper: "editor-block-tune",
      tuneTitle: "editor-block-tune-title",
      tuneInput: "editor-block-tune-input",
      tuneButton: "editor-block-tune-button",
    };

    this._dispatchChange = this.debounce(() => this.block.dispatchChange());
  }

  _getDimConfig(dimension) {
    return dimension === "width" ? this._widthConfig : this._heightConfig;
  }

  _resolveStyle(value, unit) {
    if (value === "auto" || value === "0" || value === 0 || value === "") {
      return "";
    }
    return `${value}${unit}`;
  }

  wrap(blockContent) {
    this.wrapper = document.createElement("div");
    this.wrapper.classList.add(this._CSS.blockWrapper);
    this.wrapper.append(blockContent);

    const target =
      this.wrapper.querySelector(".cdx-inline-media-wrapper") ?? this.wrapper;
    target.style.width = this._resolveStyle(
      this.data.dimensions.width,
      this.data.dimensions.widthUnit,
    );
    target.style.height = this._resolveStyle(
      this.data.dimensions.height,
      this.data.dimensions.heightUnit,
    );

    return this.wrapper;
  }

  debounce(func, timeout = 300) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func.apply(this, args);
      }, timeout);
    };
  }

  _makeUnitToggle(dimension, dimConfig) {
    const unitKey = `${dimension}Unit`;
    const toggle = document.createElement("button");

    toggle.setAttribute("type", "button");
    toggle.classList.add(this._CSS.unitToggle, this._CSS.tuneButton);
    toggle.innerHTML = this.data.dimensions[unitKey];

    if (dimConfig.unitToggle) {
      toggle.addEventListener("click", () => {
        const isPercent = this.data.dimensions[unitKey] === "%";
        this.data.dimensions[unitKey] = isPercent ? "px" : "%";
        toggle.innerHTML = this.data.dimensions[unitKey];
        BtnTooltip(toggle, `Toggle unit to ${isPercent ? "%" : "px"}`);

        const target =
          this.wrapper.querySelector(".cdx-inline-media-wrapper") ??
          this.wrapper;
        target.style[dimension] = this._resolveStyle(
          this.data.dimensions[dimension],
          this.data.dimensions[unitKey],
        );

        this._dispatchChange();
      });
    } else {
      toggle.innerHTML = "px";
    }

    return toggle;
  }

  render() {
    const wrapper = document.createElement("div");
    wrapper.classList.add(this._CSS.wrapper, this._CSS.tuneWrapper);

    const target =
      this.wrapper.querySelector(".cdx-inline-media-wrapper") ?? this.wrapper;

    const makeDimensionRow = (dimension) => {
      const isWidth = dimension === "width";
      const unitKey = `${dimension}Unit`;
      const dimConfig = this._getDimConfig(dimension);

      const container = document.createElement("div");
      container.classList.add(
        isWidth
          ? this._CSS.widthSettingContainer
          : this._CSS.heightSettingContainer,
        "d-flex",
        "flex-column",
        "align-items-start",
      );

      const label = document.createElement("label");
      label.classList.add(
        isWidth ? this._CSS.widthLabel : this._CSS.heightLabel,
        this._CSS.tuneTitle,
      );
      label.innerHTML = isWidth ? "Width" : "Height";

      const inputCol = document.createElement("div");
      inputCol.classList.add("d-flex", "flex-row", "pe-3");

      const input = document.createElement("input");
      input.classList.add(
        isWidth ? this._CSS.width : this._CSS.height,
        this._CSS.tuneInput,
      );
      input.setAttribute("type", "number");
      input.setAttribute("min", dimConfig.min);
      input.setAttribute("max", dimConfig.max);

      const currentVal = this.data.dimensions[dimension];
      if (currentVal && currentVal !== "auto") {
        input.value = currentVal;
      }

      const reset = () => {
        this.data.dimensions[dimension] = "auto";
        input.value = "";
        const target =
          this.wrapper.querySelector(".cdx-inline-media-wrapper") ??
          this.wrapper;
        target.style[dimension] = "";
        this._dispatchChange();
      };

      input.addEventListener("input", (e) => {
        const val = e.target.value;
        const target =
          this.wrapper.querySelector(".cdx-inline-media-wrapper") ??
          this.wrapper;

        if (val === "") {
          const computed = window.getComputedStyle(target);
          const computedVal = parseFloat(computed[dimension]);
          if (!isNaN(computedVal)) {
            e.target.value = Math.round(computedVal);
            this.data.dimensions[dimension] = String(Math.round(computedVal));
            target.style[dimension] = this._resolveStyle(
              this.data.dimensions[dimension],
              this.data.dimensions[unitKey],
            );
            this._dispatchChange();
            return;
          }
        }

        if (val === "0") {
          this.data.dimensions[dimension] = "auto";
        } else {
          let num = parseFloat(val);
          if (!isNaN(num)) {
            if (dimConfig.min !== null) num = Math.max(dimConfig.min, num);
            if (dimConfig.max !== null) num = Math.min(dimConfig.max, num);
            this.data.dimensions[dimension] = String(num);
          }
        }

        target.style[dimension] = this._resolveStyle(
          this.data.dimensions[dimension],
          this.data.dimensions[unitKey],
        );
        this._dispatchChange();
      });

      inputCol.appendChild(input);
      inputCol.appendChild(this._makeUnitToggle(dimension, dimConfig));
      const resetBtn = document.createElement("button");
      resetBtn.setAttribute("type", "button");
      resetBtn.classList.add(this._CSS.tuneButton);
      resetBtn.innerHTML = ResetIcon;
      BtnTooltip(resetBtn, "Reset to auto");
      resetBtn.addEventListener("click", reset);
      inputCol.appendChild(resetBtn);

      container.appendChild(label);
      container.appendChild(inputCol);
      return container;
    };

    wrapper.appendChild(makeDimensionRow("width"));
    wrapper.appendChild(makeDimensionRow("height"));

    return wrapper;
  }

  save() {
    return this.data;
  }
}
